import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { AlertTriangle } from "lucide-react";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import InfoModal from "../ui/InfoModal";
import { getOBBCorners, satPolygons } from "../../utils/geometryUtils";
import "../../styles/leather-picker.css";
import useAreaData from "../../hooks/useAreaData";
import useMinimapDimensions from "../../hooks/useMinimapDimensions";
import MobileTopBar from "./leather-picker/MobileTopBar";
import DesktopHeaderBar from "./leather-picker/DesktopHeaderBar";
import RotationControlPanel from "./leather-picker/RotationControlPanel";
import PickerMinimap from "./leather-picker/PickerMinimap";
import MobileBottomBar from "./leather-picker/MobileBottomBar";

const LeatherPicker = ({
    hides, currentHideIndex, setCurrentHideIndex, imageUrl, imgRef, centerSelector,
    isOrderFinished, isConfirmed, handleConfirmPattern, isCapturing,
    shortPos, setShortPos, longPos, setLongPos, activePart, setActivePart,
    vWidth, vShort, vLong, horizontalGap, currentTotalWidth, currentTotalHeight,
    isConfigOpen, trueWidth, trueHeight, scale,
    localesList, currentLocale, setCurrentLocale,
    cutOption, setCutOption, shortPoints, longPath, currentPrice,
    preSelectedSize, setPreSelectedSize, localCartAreas, setIsHideFullyUsed, setActiveMobileView,
    onContainerResize, isFormValid,
    strapRotation = 0, setStrapRotation
}) => {
    const { t } = useTranslation();
    const nodeShortRef = useRef(null);
    const nodeLongRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
    const [isMinimapFull, setIsMinimapFull] = useState(false);
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
    const [isDraggingMain, setIsDraggingMain] = useState(false);
    const [isImageLoading, setIsImageLoading] = useState(false);
    const [isLocaleDropdownOpen, setIsLocaleDropdownOpen] = useState(false);
    const [isRotateOpen, setIsRotateOpen] = useState(false);
    const [isGuideOpen, setIsGuideOpen] = useState(false);
    const [guideTab, setGuideTab] = useState('free');

    const minimapShortDotRef = useRef(null);
    const minimapLongDotRef = useRef(null);
    const minimapScrollRef = useRef(null);
    const animationFrameId = useRef(null);
    const isSyncingMain = useRef(false);
    const isSyncingMini = useRef(false);
    const activeMiniScrollRef = useRef({ dotX: 0, dotY: 0, containerW: 0, containerH: 0 });

    const [imgDisplayHeight, setImgDisplayHeight] = useState(0);

    useEffect(() => {
        if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
            setImgDisplayHeight(trueWidth * (imgRef.current.naturalHeight / imgRef.current.naturalWidth));
        }
    }, [imageUrl, trueWidth, imgRef]);

    const yScale = trueHeight > 0 ? imgDisplayHeight / trueHeight : 1;

    const currentHide = hides[currentHideIndex] || {};

    const {
        restrictedAreas, preSelectedAreas,
        allUsedAreas,
        hasFreeMovePurchase, hasPreSelectedPurchase,
    } = useAreaData({
        currentHide, trueWidth, trueHeight, imgDisplayHeight,
        vWidth, vShort, vLong, yScale, scale,
        localCartAreas, cutOption, isConfirmed, isOrderFinished,
    });

    const isFreeMoveLocked = cutOption === 'free' && hasFreeMovePurchase;

    const checkCollision = useCallback((posX, posY, w, h) => {
        if (!trueWidth || !imgDisplayHeight) return false;

        // Helper: convert a stored area to display-space rect
        // Free move: h is already strap height (no yScale). DB/preselected: h needs yScale.
        const areaToDisplay = (a) => ({
            x: a.x,
            y: a.y * yScale,
            w: a.w,
            h: a.isFreeMove ? a.h : a.h * yScale,
        });

        if (strapRotation === 0) {
            const rect = { x: posX, y: posY * yScale, w, h };
            const isOv = (a, b) => (a.x < b.x + b.w - 2 && a.x + a.w > b.x + 2 && a.y < b.y + b.h - 2 && a.y + a.h > b.y + 2);
            const hasDbCollision = allUsedAreas.some(a => isOv(rect, areaToDisplay(a)));
            const hasRestricted = restrictedAreas.some(r => isOv(rect, { x: r.x * trueWidth, y: r.y * imgDisplayHeight, w: r.w * trueWidth, h: r.h * imgDisplayHeight }));
            const isOutOfBounds = rect.x < 0 || rect.y < 0 || (rect.x + rect.w) > trueWidth || (rect.y + rect.h) > imgDisplayHeight + 2;
            return hasDbCollision || hasRestricted || isOutOfBounds;
        }

        // OBB collision using SAT
        const cx = posX + w / 2, cy = posY * yScale + h / 2;
        const strapC = getOBBCorners(cx, cy, w, h, strapRotation);

        const hasDbCollision = allUsedAreas.some(a => {
            const d = areaToDisplay(a);
            return satPolygons(strapC, getOBBCorners(d.x + d.w / 2, d.y + d.h / 2, d.w, d.h, 0));
        });
        const hasRestricted = restrictedAreas.some(r => {
            const rx = r.x * trueWidth, ry = r.y * imgDisplayHeight, rw = r.w * trueWidth, rh = r.h * imgDisplayHeight;
            return satPolygons(strapC, getOBBCorners(rx + rw / 2, ry + rh / 2, rw, rh, 0));
        });
        // Out-of-bounds: check all 4 rotated corners
        const isOutOfBounds = strapC.some(p => p.x < 0 || p.y < 0 || p.x > trueWidth || p.y > imgDisplayHeight + 2);

        return hasDbCollision || hasRestricted || isOutOfBounds;
    }, [allUsedAreas, restrictedAreas, trueWidth, imgDisplayHeight, yScale, strapRotation]);

    const checkPreselectedCollision = useCallback((posX, posY, w, h) => {
        if (!trueWidth || !imgDisplayHeight) return null;

        // Helper: convert a stored area to display-space rect
        const areaToDisplay = (a) => ({
            x: a.x,
            y: a.y * yScale,
            w: a.w,
            h: a.isFreeMove ? a.h : a.h * yScale,
        });

        if (strapRotation === 0) {
            const rect = { x: posX, y: posY * yScale, w, h };
            const isOv = (a, b) => (a.x < b.x + b.w - 2 && a.x + a.w > b.x + 2 && a.y < b.y + b.h - 2 && a.y + a.h > b.y + 2);

            const hasDbCollision = allUsedAreas.some(a => isOv(rect, areaToDisplay(a)));
            if (hasDbCollision) return 'db';

            const isOutOfBounds = rect.x < 0 || rect.y < 0 || (rect.x + rect.w) > trueWidth || (rect.y + rect.h) > imgDisplayHeight + 2;
            if (isOutOfBounds) return 'outOfBounds';

            return null;
        }

        const cx = posX + w / 2, cy = posY * yScale + h / 2;
        const strapC = getOBBCorners(cx, cy, w, h, strapRotation);

        const hasDbCollision = allUsedAreas.some(a => {
            const d = areaToDisplay(a);
            return satPolygons(strapC, getOBBCorners(d.x + d.w / 2, d.y + d.h / 2, d.w, d.h, 0));
        });
        if (hasDbCollision) return 'db';

        const isOutOfBounds = strapC.some(p => p.x < 0 || p.y < 0 || p.x > trueWidth || p.y > imgDisplayHeight + 2);
        if (isOutOfBounds) return 'outOfBounds';

        return null;
    }, [allUsedAreas, trueWidth, imgDisplayHeight, yScale, strapRotation]);

    const availablePreselectedAreas = useMemo(() => {
        if (!preSelectedAreas || preSelectedAreas.length === 0) return [];
        return preSelectedAreas.filter(p => {
            // Một group là "available" nếu chưa có đơn hàng nào đánh dấu isUsed
            if (p.isUsed === true) return false;
            // Kiểm tra thêm trong allUsedAreas (local cart)
            return !allUsedAreas.some(u => u.id && u.id.startsWith(p.uniqueAreaId));
        });
    }, [preSelectedAreas, allUsedAreas]);

    const isAllPreselectedUsed = cutOption === 'preselected' && preSelectedAreas.length > 0 && availablePreselectedAreas.length === 0;

    const collisionType = useMemo(() => {
        if (isAllPreselectedUsed) return null;

        if (cutOption === 'preselected') {
            if (!preSelectedSize) return null;
            const shortCol = checkPreselectedCollision(shortPos.x, shortPos.y, vWidth, vShort);
            const longCol = checkPreselectedCollision(longPos.x, longPos.y, vWidth, vLong);
            if (shortCol === 'db' || longCol === 'db') {
                return 'ZONE OVERLAPS WITH USED AREA';
            }
            if (shortCol === 'outOfBounds' || longCol === 'outOfBounds') {
                return 'ZONE IS OUT OF BOUNDS';
            }
            return null;
        }

        if (isFreeMoveLocked) return null;
        const shortCollision = checkCollision(shortPos.x, shortPos.y, vWidth, vShort);
        const longCollision = checkCollision(longPos.x, longPos.y, vWidth, vLong);

        let strapsOverlap;
        if (strapRotation === 0) {
            const isOv = (a, b) => (a.x < b.x + b.w - 2 && a.x + a.w > b.x + 2 && a.y < b.y + b.h - 2 && a.y + a.h > b.y + 2);
            strapsOverlap = isOv(
                { x: shortPos.x, y: shortPos.y * yScale, w: vWidth, h: vShort },
                { x: longPos.x, y: longPos.y * yScale, w: vWidth, h: vLong }
            );
        } else {
            strapsOverlap = satPolygons(
                getOBBCorners(shortPos.x + vWidth / 2, shortPos.y * yScale + vShort / 2, vWidth, vShort, strapRotation),
                getOBBCorners(longPos.x + vWidth / 2, longPos.y * yScale + vLong / 2, vWidth, vLong, strapRotation)
            );
        }

        if (shortCollision && activePart === 'short') return 'SHORT STRAP OVERLAP';
        if (longCollision && activePart === 'long') return 'LONG STRAP OVERLAP';
        if (strapsOverlap) return 'STRAPS OVERLAP';
        if (shortCollision || longCollision) return 'OVERLAP DETECTED';
        return null;
    }, [cutOption, isFreeMoveLocked, isAllPreselectedUsed, preSelectedSize, shortPos, longPos, vWidth, vShort, vLong, activePart, checkCollision, checkPreselectedCollision, yScale, strapRotation]);

    useEffect(() => {
        if (typeof setIsHideFullyUsed === 'function') {
            if (cutOption === 'free') {
                setIsHideFullyUsed(hasFreeMovePurchase);
            } else if (cutOption === 'preselected') {
                setIsHideFullyUsed(preSelectedAreas.length > 0 && availablePreselectedAreas.length === 0);
            }
        }
    }, [cutOption, hasFreeMovePurchase, preSelectedAreas.length, availablePreselectedAreas.length, setIsHideFullyUsed]);

    const displayZoom = useMemo(() => {
        if (containerSize.w > 0 && trueWidth > 0) {
            return containerSize.w / trueWidth;
        }
        return 1.0;
    }, [containerSize.w, trueWidth]);

    const snapToFirstAvailablePreselected = useCallback((forcedIndex = null) => {
        if (hasFreeMovePurchase) return;

        if (availablePreselectedAreas.length > 0) {
            let targetGroup;
            // Chỉ dùng forcedIndex nếu zone đó vẫn còn available (chưa bị IN CART / SOLD)
            if (forcedIndex !== null && forcedIndex !== undefined && preSelectedAreas[forcedIndex]) {
                const candidate = preSelectedAreas[forcedIndex];
                const isAvailable = availablePreselectedAreas.some(a => a.uniqueAreaId === candidate.uniqueAreaId);
                targetGroup = isAvailable ? candidate : null;
            }
            // Nếu forcedIndex không hợp lệ hoặc zone đã dùng → chọn default hoặc đầu tiên còn trống
            if (!targetGroup) {
                targetGroup = availablePreselectedAreas.find(a => a.isDefault === true) || availablePreselectedAreas[0];
            }

            // Snap dây ngắn (Short) vào shortEnd
            const sX = targetGroup.shortEnd.x * trueWidth;
            const sY = targetGroup.shortEnd.y * trueHeight;
            setShortPos({ x: sX, y: sY });

            // Snap dây dài (Long) vào longEnd
            const lX = targetGroup.longEnd.x * trueWidth;
            const lY = targetGroup.longEnd.y * trueHeight;
            setLongPos({ x: lX, y: lY });

            setPreSelectedSize({
                slotIndex: preSelectedAreas.indexOf(targetGroup),
                uniqueAreaId: targetGroup.uniqueAreaId,
                xRatio: targetGroup.longEnd.x,
                yRatio: targetGroup.longEnd.y,
                wRatio: targetGroup.longEnd.width,
                hRatio: targetGroup.longEnd.height,
                shortXRatio: targetGroup.shortEnd.x,
                shortYRatio: targetGroup.shortEnd.y,
                shortWRatio: targetGroup.shortEnd.width,
                shortHRatio: targetGroup.shortEnd.height,
            });
        }
    }, [availablePreselectedAreas, preSelectedAreas, hasFreeMovePurchase, setShortPos, setLongPos, setPreSelectedSize, trueWidth, trueHeight]);

    const prevHideIndex = useRef(currentHideIndex);
    const slotIndex = preSelectedSize?.slotIndex;

    useEffect(() => {
        if (trueWidth > 0 && trueHeight > 0) {
            const isHideChanged = prevHideIndex.current !== currentHideIndex;
            prevHideIndex.current = currentHideIndex;

            if (isHideChanged) {
                if (cutOption === 'preselected') {
                    snapToFirstAvailablePreselected(slotIndex);
                } else if (cutOption === 'free') {
                    centerSelector();
                }
            }
        }
    }, [currentHideIndex, cutOption, trueWidth, trueHeight, snapToFirstAvailablePreselected, centerSelector, slotIndex]);
    useEffect(() => {
        if (cutOption === 'preselected' && slotIndex !== undefined) {
            const targetGroup = preSelectedAreas[slotIndex];
            if (targetGroup) {
                // Chỉ snap nếu zone này vẫn còn available (chưa bị IN CART / SOLD)
                const isAvailable = availablePreselectedAreas.some(a => a.uniqueAreaId === targetGroup.uniqueAreaId);
                if (!isAvailable) return;
                // Snap dây ngắn vào shortEnd
                setShortPos({
                    x: targetGroup.shortEnd.x * trueWidth,
                    y: targetGroup.shortEnd.y * trueHeight,
                });
                // Snap dây dài vào longEnd
                setLongPos({
                    x: targetGroup.longEnd.x * trueWidth,
                    y: targetGroup.longEnd.y * trueHeight,
                });
            }
        }
    }, [currentTotalWidth, currentTotalHeight, cutOption, preSelectedAreas, availablePreselectedAreas, slotIndex, setShortPos, setLongPos, trueHeight, trueWidth]);

    useEffect(() => {
        if (cutOption === 'preselected' && preSelectedSize && preSelectedAreas.length > 0 && trueWidth > 0 && trueHeight > 0) {
            const isRestoredZoneAvailable = availablePreselectedAreas.some(
                a => a.uniqueAreaId === preSelectedSize.uniqueAreaId
            );
            if (!isRestoredZoneAvailable) {
                setPreSelectedSize(null);
                snapToFirstAvailablePreselected();
            }
        }
    }, [cutOption, preSelectedSize, preSelectedAreas, availablePreselectedAreas, trueWidth, trueHeight, snapToFirstAvailablePreselected, setPreSelectedSize]);

    useEffect(() => {
        if (hasPreSelectedPurchase && cutOption === 'free') {
            setCutOption('preselected');
        }
    }, [hasPreSelectedPurchase, cutOption, setCutOption]);

    // Snap to first available slot on initial load or when cutOption switches to preselected
    useEffect(() => {
        if (cutOption === 'preselected' && !preSelectedSize && availablePreselectedAreas.length > 0 && trueWidth > 0 && trueHeight > 0) {
            snapToFirstAvailablePreselected();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cutOption, trueWidth, trueHeight, availablePreselectedAreas.length]);

    useEffect(() => {
        setIsImageLoading(true);
        const timer = setTimeout(() => { setIsImageLoading(false); }, 3000);
        return () => clearTimeout(timer);
    }, [currentHideIndex]);

    const smoothScrollTo = useCallback((container, targetX, targetY, duration = 700) => {
        if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
        const startX = container.scrollLeft; const startY = container.scrollTop;
        const distanceX = targetX - startX; const distanceY = targetY - startY;
        let startTime = null;

        const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);
        const animation = (currentTime) => {
            if (startTime === null) startTime = currentTime;
            const progress = Math.min((currentTime - startTime) / duration, 1);
            container.scrollTo({ left: startX + distanceX * easeOutQuart(progress), top: startY + distanceY * easeOutQuart(progress), behavior: 'auto' });
            if (progress < 1) animationFrameId.current = requestAnimationFrame(animation);
            else animationFrameId.current = null;
        };
        animationFrameId.current = requestAnimationFrame(animation);
    }, []);

    const scrollToTargetPosition = useCallback((posX, posY) => {
        if (scrollContainerRef.current && trueWidth > 0 && trueHeight > 0) {
            const container = scrollContainerRef.current;
            const scrollX = (posX * displayZoom) - (container.clientWidth / 2) + ((currentTotalWidth * displayZoom) / 2);
            const scrollY = (posY * yScale * displayZoom) - (container.clientHeight / 2) + ((currentTotalHeight * displayZoom) / 2);
            smoothScrollTo(container, scrollX, scrollY, 700);
        }
    }, [trueWidth, trueHeight, displayZoom, currentTotalWidth, currentTotalHeight, smoothScrollTo, yScale]);

    const handleImageLoad = (e) => {
        setIsImageLoading(false);
        if (e.target && e.target.naturalWidth > 0) {
            setImgDisplayHeight(trueWidth * (e.target.naturalHeight / e.target.naturalWidth));
        }
        const pos = activePart === 'long' ? longPos : shortPos;
        setTimeout(() => scrollToTargetPosition(pos.x, pos.y), 100);
    };

    useEffect(() => {
        const pos = activePart === 'long' ? longPos : shortPos;
        const timer = setTimeout(() => { scrollToTargetPosition(pos.x, pos.y); }, 150);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentHideIndex, cutOption, activePart]);

    useEffect(() => {
        if (containerSize.w > 0 && trueWidth > 0) {
            const pos = activePart === 'long' ? longPos : shortPos;
            scrollToTargetPosition(pos.x, pos.y);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayZoom]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            if (scrollContainerRef.current) {
                const w = scrollContainerRef.current.clientWidth;
                const h = scrollContainerRef.current.clientHeight;
                setContainerSize({ w, h });
                if (typeof onContainerResize === 'function') onContainerResize(w);
            }
        };
        window.addEventListener('resize', handleResize); handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, [onContainerResize]);

    useEffect(() => {
        if (!isMinimapFull || !minimapScrollRef.current) return;
        const timeoutId = setTimeout(() => {
            if (!minimapScrollRef.current) return;
            const { dotX, dotY, containerW, containerH } = activeMiniScrollRef.current;
            minimapScrollRef.current.scrollLeft = dotX - containerW / 2;
            minimapScrollRef.current.scrollTop = dotY - containerH / 2;
        }, 50);
        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMinimapFull]);

    const showShortLongBar = cutOption === 'free' && !isConfirmed && !isOrderFinished && !isFreeMoveLocked;

    const {
        minimapContainerW, minimapContainerH,
        minimapImageW, minimapImageH,
        dotShortX, dotShortY, dotLongX, dotLongY,
        boundsShort, boundsLong,
        miniScale, DOT_RADIUS,
    } = useMinimapDimensions({
        trueWidth, imgDisplayHeight, containerSize, isMobile,
        shortPos, longPos, yScale, vWidth, vShort, vLong, isMinimapFull,
    });

    activeMiniScrollRef.current = {
        dotX: (activePart === 'long' ? dotLongX : dotShortX) + DOT_RADIUS,
        dotY: (activePart === 'long' ? dotLongY : dotShortY) + DOT_RADIUS,
        containerW: minimapContainerW,
        containerH: minimapContainerH,
    };

    const handleMinimapDrag = (data, partType) => {
        if (cutOption === 'preselected' || isFreeMoveLocked || isAllPreselectedUsed) return;

        const centerX = data.x + DOT_RADIUS;
        const centerY = data.y + DOT_RADIUS;
        const currentH = partType === 'long' ? vLong : vShort;

        const trueX = (centerX / miniScale) - (vWidth / 2);
        const trueY_disp = (centerY / miniScale) - (currentH / 2);
        const trueY = trueY_disp / yScale;

        if (partType === 'long') setLongPos({ x: trueX, y: trueY });
        else setShortPos({ x: trueX, y: trueY });

        if (activePart === partType && scrollContainerRef.current) {
            if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
            scrollContainerRef.current.scrollTo({
                left: (trueX * displayZoom) - (scrollContainerRef.current.clientWidth / 2) + ((currentTotalWidth * displayZoom) / 2),
                top: (trueY_disp * displayZoom) - (scrollContainerRef.current.clientHeight / 2) + ((currentTotalHeight * displayZoom) / 2),
                behavior: 'auto'
            });
        }
    };

    const handleMainScroll = (e) => {
        const main = e.target;
        const mini = minimapScrollRef.current;
        if (!mini) return;

        if (isSyncingMini.current) {
            isSyncingMini.current = false;
            return;
        }
        isSyncingMain.current = true;
        const scrollLeftPercent = main.scrollLeft / (main.scrollWidth - main.clientWidth || 1);
        const scrollTopPercent = main.scrollTop / (main.scrollHeight - main.clientHeight || 1);

        mini.scrollLeft = scrollLeftPercent * (mini.scrollWidth - mini.clientWidth);
        mini.scrollTop = scrollTopPercent * (mini.scrollHeight - mini.clientHeight);
    };

    const handleMiniScroll = (e) => {
        if (!isMinimapFull) return;
        const mini = e.target;
        const main = scrollContainerRef.current;
        if (!main) return;

        if (isSyncingMain.current) {
            isSyncingMain.current = false;
            return;
        }
        isSyncingMini.current = true;
        const scrollLeftPercent = mini.scrollLeft / (mini.scrollWidth - mini.clientWidth || 1);
        const scrollTopPercent = mini.scrollTop / (mini.scrollHeight - mini.clientHeight || 1);

        main.scrollLeft = scrollLeftPercent * (main.scrollWidth - main.clientWidth);
        main.scrollTop = scrollTopPercent * (main.scrollHeight - main.clientHeight);
    };

    const strokeWidth = 3;
    const borderColor = isOrderFinished ? '#ffffff' : (collisionType ? '#dc3545' : (isConfirmed ? '#22c55e' : '#C5A075'));

    const isConfirmDisabled = !isFormValid || collisionType || isConfigOpen || isCapturing || isFreeMoveLocked || isAllPreselectedUsed || hasFreeMovePurchase;
    let confirmBtnText = t('btn_confirm_pattern', 'CONFIRM PATTERN');
    if (isCapturing) confirmBtnText = t('btn_capturing', 'CAPTURING...');
    else if (!isFormValid) confirmBtnText = t('required_missing', 'PLEASE SELECT REQUIRED');
    else if (hasFreeMovePurchase) confirmBtnText = t('free_move_locked', 'MAX 1 CUT ALLOWED');
    else if (isAllPreselectedUsed) confirmBtnText = t('all_used', 'ALL AREAS USED');
    else if (collisionType) confirmBtnText = t('msg_cant_select', "CAN'T SELECT");

    return (
        <div className="w-100 h-100 p-0 p-md-4 d-flex flex-column position-relative" style={{ backgroundColor: '#FCFBF7' }}>

            {collisionType && !isConfirmed && !isOrderFinished && !isFreeMoveLocked && !isAllPreselectedUsed && (
                <div className="collision-warning-banner" style={{ backgroundColor: '#dc3545', zIndex: 1200 }}>
                    <AlertTriangle size={16} className="me-2" />
                    {t(collisionType, collisionType)}
                </div>
            )}

            {/* Floating Short/Long selector — desktop only */}
            {showShortLongBar && (
                <div className="d-none d-md-block" style={{ position: 'absolute', bottom: '36px', left: '92px', zIndex: 80 }}>
                    <div className="btn-group shadow-sm p-1" style={{ backgroundColor: '#F6F5F0', borderRadius: '50px', border: '1px solid rgba(233, 228, 219, 0.8)', height: '42px' }}>
                        <button className={`btn rounded-pill fw-bold px-4 transition-all ${activePart === 'short' ? 'text-white' : 'btn-light bg-transparent text-muted border-0'}`} onClick={() => setActivePart('short')} style={{ fontSize: '0.875rem', backgroundColor: activePart === 'short' ? '#C5A075' : 'transparent' }}>Short</button>
                        <button className={`btn rounded-pill fw-bold px-4 transition-all ${activePart === 'long' ? 'text-white' : 'btn-light bg-transparent text-muted border-0'}`} onClick={() => setActivePart('long')} style={{ fontSize: '0.875rem', backgroundColor: activePart === 'long' ? '#C5A075' : 'transparent' }}>Long</button>
                    </div>
                </div>
            )}

            <MobileTopBar
                hides={hides}
                currentHideIndex={currentHideIndex}
                setCurrentHideIndex={setCurrentHideIndex}
                isOrderFinished={isOrderFinished}
                isConfigOpen={isConfigOpen}
                isCapturing={isCapturing}
                isConfirmDisabled={isConfirmDisabled}
                confirmBtnText={confirmBtnText}
                handleConfirmPattern={handleConfirmPattern}
                collisionType={collisionType}
                hasFreeMovePurchase={hasFreeMovePurchase}
                isAllPreselectedUsed={isAllPreselectedUsed}
                currentHide={currentHide}
                isFormValid={isFormValid}
                t={t}
            />

            <DesktopHeaderBar
                hides={hides}
                currentHideIndex={currentHideIndex}
                setCurrentHideIndex={setCurrentHideIndex}
                cutOption={cutOption}
                setCutOption={setCutOption}
                isConfirmed={isConfirmed}
                isOrderFinished={isOrderFinished}
                isConfigOpen={isConfigOpen}
                isCapturing={isCapturing}
                confirmBtnText={confirmBtnText}
                isConfirmDisabled={isConfirmDisabled}
                collisionType={collisionType}
                hasFreeMovePurchase={hasFreeMovePurchase}
                isAllPreselectedUsed={isAllPreselectedUsed}
                currentHide={currentHide}
                currentPrice={currentPrice}
                hasPreSelectedPurchase={hasPreSelectedPurchase}
                handleConfirmPattern={handleConfirmPattern}
                localesList={localesList}
                currentLocale={currentLocale}
                setCurrentLocale={setCurrentLocale}
                isLocaleDropdownOpen={isLocaleDropdownOpen}
                setIsLocaleDropdownOpen={setIsLocaleDropdownOpen}
                setGuideTab={setGuideTab}
                setIsGuideOpen={setIsGuideOpen}
                setIsRotateOpen={setIsRotateOpen}
                t={t}
            />

            <div className="position-relative flex-grow-1 w-100" style={{ minHeight: 0 }}>
                {isImageLoading && (
                    <div className="position-absolute w-100 h-100 d-flex flex-column justify-content-center align-items-center" style={{ top: 0, left: 0, zIndex: 2000, backgroundColor: 'rgba(252, 251, 247, 0.85)', backdropFilter: 'blur(5px)', borderRadius: '28px' }}>
                        <div className="spinner-border mb-3" style={{ color: '#C5A075', width: '3rem', height: '3rem', borderWidth: '0.2em' }} role="status"></div>
                        <span className="fw-bold" style={{ color: '#8B6914', letterSpacing: '3px', fontSize: '0.85rem' }}>{t('loading_texture', 'LOADING TEXTURE...')}</span>
                    </div>
                )}

                <div ref={scrollContainerRef} onScroll={handleMainScroll} className="picker-scroll-container w-100 h-100 bg-white shadow-sm position-relative" style={{ borderRadius: '28px', border: '1px solid rgba(233, 228, 219, 0.8)', overflowX: 'hidden', overflowY: 'auto' }}>
                    {/* Padding for Mobile Top/Bottom Bars */}
                    <div className="d-block d-md-none" style={{ height: '70px' }}></div>

                    <div style={{ width: `${trueWidth * displayZoom}px`, height: `${imgDisplayHeight * displayZoom}px`, position: 'relative', minWidth: '100%', minHeight: '100%' }}>
                        <div style={{ position: 'absolute', top: `${containerSize.h > imgDisplayHeight * displayZoom ? (containerSize.h - imgDisplayHeight * displayZoom) / 2 : 0}px`, left: 0, width: `${trueWidth}px`, height: `${imgDisplayHeight}px`, transform: `scale(${displayZoom})`, transformOrigin: 'top left' }}>

                            <img ref={imgRef} src={imageUrl} onLoad={handleImageLoad} alt="Leather" decoding="async" style={{ width: '100%', height: '100%', display: 'block', objectFit: 'fill', filter: currentHide.isHidden ? 'grayscale(1) opacity(0.4) blur(1px)' : 'none' }} draggable="false" crossOrigin="anonymous" />
                            {(isConfirmed || isOrderFinished || currentHide.isHidden) && <div className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center" style={{ top: 0, left: 0, backgroundColor: currentHide.isHidden ? 'rgba(0,0,0,0.1)' : 'rgba(26, 17, 11, 0.4)', zIndex: 40, backdropFilter: 'grayscale(40%) blur(2px)' }}>
                                {currentHide.isHidden && (
                                    <div className="bg-danger text-white px-4 py-2 rounded-pill fw-bold shadow-lg" style={{ letterSpacing: '2px', fontSize: '1.2rem', transform: 'rotate(-5deg)', border: '4px double rgba(255,255,255,0.5)' }}>
                                        {t('sold_out', 'SOLD OUT')}
                                    </div>
                                )}
                            </div>}


                            {allUsedAreas.map((area) => {
                                // If endType is known → always display at user's current config dimensions
                                // (vWidth × vShort for short end, vWidth × vLong for long end)
                                // Legacy SOLD areas (no endType) → use stored dimensions
                                const displayW = area.endType ? vWidth : area.w;
                                const displayH = area.endType
                                    ? (area.endType === 'short' ? vShort : vLong)
                                    : (area.isFreeMove ? area.h : area.h * yScale);
                                return (
                                    <div key={area.id} style={{ position: 'absolute', left: `${area.x}px`, top: `${area.y * yScale}px`, width: `${displayW}px`, height: `${displayH}px`, backgroundColor: 'rgba(220, 53, 69, 0.5)', border: '2px solid #dc3545', zIndex: 50, display: 'flex', justifyContent: 'center', alignItems: 'center', pointerEvents: 'none' }}>
                                        <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold', background: '#dc3545', padding: '2px 8px', borderRadius: '4px', textTransform: 'uppercase' }}>
                                            {area.label === 'IN CART' ? t('in_cart', 'IN CART') : t('sold', 'SOLD')}
                                        </span>
                                    </div>
                                );
                            })}

                            {cutOption === 'free' && restrictedAreas.map((rect, idx) => (
                                <div key={`restricted-${idx}`} style={{
                                    position: 'absolute',
                                    left: `${rect.x * trueWidth}px`,
                                    top: `${rect.y * imgDisplayHeight}px`,
                                    width: `${rect.w * trueWidth}px`,
                                    height: `${rect.h * imgDisplayHeight}px`,
                                    backgroundColor: 'rgba(220, 53, 69, 0.45)',
                                    border: '3px dashed #dc3545',
                                    zIndex: 50,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    pointerEvents: 'none',
                                    borderRadius: '4px'
                                }}>
                                    <span className="fw-bold text-white text-center" style={{ fontSize: '12px', textShadow: '0 2px 4px rgba(0,0,0,0.8)', padding: '4px' }}>{t('not_allowed', 'NOT ALLOWED')}</span>
                                </div>
                            ))}

                            {cutOption === 'preselected' && preSelectedAreas.map((group, idx) => {
                                const isUsed = allUsedAreas.some(u =>
                                    u.id && u.id.startsWith(group.uniqueAreaId)
                                );
                                const isSelected = preSelectedSize?.slotIndex === idx;

                                if (isUsed) return null;
                                if (hasFreeMovePurchase) return null;
                                if (isSelected) return null;

                                const lX = group.longEnd.x * trueWidth;
                                const lY = group.longEnd.y * imgDisplayHeight;
                                const lW = group.longEnd.width * trueWidth;
                                const lH = group.longEnd.height * imgDisplayHeight;

                                const handleClick = () => {
                                    setShortPos({ x: group.shortEnd.x * trueWidth, y: group.shortEnd.y * trueHeight });
                                    setLongPos({ x: group.longEnd.x * trueWidth, y: group.longEnd.y * trueHeight });
                                    setPreSelectedSize({
                                        slotIndex: idx,
                                        uniqueAreaId: group.uniqueAreaId,
                                        xRatio: group.longEnd.x,
                                        yRatio: group.longEnd.y,
                                        wRatio: group.longEnd.width,
                                        hRatio: group.longEnd.height,
                                        shortXRatio: group.shortEnd.x,
                                        shortYRatio: group.shortEnd.y,
                                        shortWRatio: group.shortEnd.width,
                                        shortHRatio: group.shortEnd.height,
                                    });
                                    setTimeout(() => scrollToTargetPosition(lX, group.longEnd.y * trueHeight), 50);
                                };

                                const btnX = lX + lW / 2;
                                const btnY = lY + lH / 2;

                                return (
                                    <div key={`pre-ui-${idx}`}
                                        onClick={handleClick}
                                        className="zone-badge-btn"
                                        style={{ position: 'absolute', left: `${btnX}px`, top: `${btnY}px`, cursor: isConfirmed ? 'default' : 'pointer' }}>
                                        ZONE {idx + 1}
                                    </div>
                                );
                            })}

                            {cutOption === 'preselected' && preSelectedSize && !isAllPreselectedUsed && (
                                <>
                                    <div className="position-absolute" style={{
                                        top: 0, left: 0,
                                        width: `${vWidth}px`, height: `${vShort}px`,
                                        transform: `translate(${shortPos.x}px, ${shortPos.y * yScale}px)`,
                                        zIndex: 110, pointerEvents: 'none'
                                    }}>
                                        <div style={{ width: '100%', height: '100%', transform: `rotate(${strapRotation}deg)`, transformOrigin: 'center center' }}>
                                            <svg width={vWidth} height={vShort} style={{ overflow: 'visible' }}>
                                                <polygon points={shortPoints} fill="rgba(255,255,255,0.15)" stroke={borderColor} strokeWidth={strokeWidth} strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="position-absolute" style={{
                                        top: 0, left: 0,
                                        width: `${vWidth}px`, height: `${vLong}px`,
                                        transform: `translate(${longPos.x}px, ${longPos.y * yScale}px)`,
                                        zIndex: 110, pointerEvents: 'none'
                                    }}>
                                        <div style={{ width: '100%', height: '100%', transform: `rotate(${strapRotation}deg)`, transformOrigin: 'center center' }}>
                                            <svg width={vWidth} height={vLong} style={{ overflow: 'visible' }}>
                                                <path d={longPath} fill="rgba(255,255,255,0.15)" stroke={borderColor} strokeWidth={strokeWidth} strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                    </div>
                                </>
                            )}

                            {cutOption === 'free' && !isFreeMoveLocked && !isAllPreselectedUsed && (
                                <>
                                    <Draggable
                                        nodeRef={nodeShortRef} bounds="parent" position={{ x: shortPos.x, y: shortPos.y * yScale }}
                                        onStart={() => { setIsDraggingMain(true); if (activePart !== 'short') setActivePart('short'); }}
                                        onDrag={(e, data) => setShortPos({ x: data.x, y: data.y / yScale })}
                                        onStop={() => setIsDraggingMain(false)}
                                        disabled={isConfirmed || isOrderFinished || isConfigOpen}
                                        scale={displayZoom}
                                    >
                                        <div ref={nodeShortRef} className="position-absolute" onClick={() => { if (activePart !== 'short') setActivePart('short'); }} style={{
                                            top: 0, left: 0, width: vWidth, height: vShort, zIndex: activePart === 'short' ? 110 : 100,
                                            cursor: activePart === 'short' ? 'grab' : 'pointer',
                                            opacity: 1,
                                            pointerEvents: 'auto'
                                        }}>
                                            <div style={{ width: '100%', height: '100%', transform: `rotate(${strapRotation}deg)`, transformOrigin: 'center center' }}>
                                                <svg width={vWidth} height={vShort} style={{ overflow: 'visible' }}>
                                                    <polygon points={shortPoints} fill={activePart === 'short' ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.22)'} stroke={activePart === 'short' ? borderColor : '#777'} strokeWidth={activePart === 'short' ? strokeWidth : strokeWidth * 1.1} strokeLinejoin="round" style={{ backdropFilter: 'brightness(1.1)' }} />
                                                </svg>
                                            </div>
                                        </div>
                                    </Draggable>

                                    {/* LONG STRAP DRAGGABLE */}
                                    <Draggable
                                        nodeRef={nodeLongRef} bounds="parent" position={{ x: longPos.x, y: longPos.y * yScale }}
                                        onStart={() => { setIsDraggingMain(true); if (activePart !== 'long') setActivePart('long'); }}
                                        onDrag={(e, data) => setLongPos({ x: data.x, y: data.y / yScale })}
                                        onStop={() => setIsDraggingMain(false)}
                                        disabled={isConfirmed || isOrderFinished || isConfigOpen}
                                        scale={displayZoom}
                                    >
                                        <div ref={nodeLongRef} className="position-absolute" onClick={() => { if (activePart !== 'long') setActivePart('long'); }} style={{
                                            top: 0, left: 0, width: vWidth, height: vLong, zIndex: activePart === 'long' ? 110 : 100,
                                            cursor: activePart === 'long' ? 'grab' : 'pointer',
                                            opacity: 1,
                                            pointerEvents: 'auto'
                                        }}>
                                            <div style={{ width: '100%', height: '100%', transform: `rotate(${strapRotation}deg)`, transformOrigin: 'center center' }}>
                                                <svg width={vWidth} height={vLong} style={{ overflow: 'visible' }}>
                                                    <path d={longPath} fill={activePart === 'long' ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.22)'} stroke={activePart === 'long' ? borderColor : '#777'} strokeWidth={activePart === 'long' ? strokeWidth : strokeWidth * 1.1} strokeLinejoin="round" style={{ backdropFilter: 'brightness(1.1)' }} />
                                                </svg>
                                            </div>
                                        </div>
                                    </Draggable>
                                </>
                            )}

                        </div>
                    </div>

                    {/* Padding for Mobile Bottom Bar */}
                    <div className="d-block d-md-none" style={{ height: '90px' }}></div>
                </div>
            </div>

            <RotationControlPanel
                strapRotation={strapRotation}
                setStrapRotation={setStrapRotation}
                isRotateOpen={isRotateOpen}
                setIsRotateOpen={setIsRotateOpen}
                isMobile={isMobile}
                isOrderFinished={isOrderFinished}
                isFreeMoveLocked={isFreeMoveLocked}
                cutOption={cutOption}
            />


            {isMinimapFull && (
                <div onClick={() => setIsMinimapFull(false)} className="position-absolute w-100 h-100" style={{ top: 0, left: 0, zIndex: 1050, backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', animation: 'minimapBackdropFade 0.3s ease-out forwards' }} />
            )}


            {/* ── Mode Guide Modal ── */}
            <InfoModal
                isOpen={isGuideOpen}
                onClose={() => setIsGuideOpen(false)}
                category="HOW IT WORKS"
                title={guideTab === 'free' ? 'Free Move' : 'Pre-selected'}
                subtitle={guideTab === 'free' ? 'Place your straps anywhere on the hide' : 'Choose from expert-curated cut zones'}
                tabs={[
                    { id: 'free', label: 'Free Move' },
                    { id: 'preselected', label: 'Pre-selected' }
                ]}
                activeTab={guideTab}
                onTabChange={setGuideTab}
                maxWidth="520px"
            >
                {guideTab === 'free' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', gap: '12px' }}><span style={{ color: '#C5A075', fontWeight: '700', flexShrink: 0, minWidth: '22px' }}>01</span><span>Drag the <strong>Short</strong> and <strong>Long</strong> strap outlines to your preferred position on the leather.</span></div>
                        <div style={{ display: 'flex', gap: '12px' }}><span style={{ color: '#C5A075', fontWeight: '700', flexShrink: 0, minWidth: '22px' }}>02</span><span>Use the <strong>Short / Long</strong> toggle at the bottom to switch which strap you're moving.</span></div>
                        <div style={{ display: 'flex', gap: '12px' }}><span style={{ color: '#C5A075', fontWeight: '700', flexShrink: 0, minWidth: '22px' }}>03</span><span>Red <strong>NOT ALLOWED</strong> zones indicate damaged areas — straps cannot be placed there.</span></div>
                        <div style={{ display: 'flex', gap: '12px' }}><span style={{ color: '#C5A075', fontWeight: '700', flexShrink: 0, minWidth: '22px' }}>04</span><span>Use the <strong>↻</strong> button on the left to rotate the strap orientation.</span></div>
                        <div style={{ display: 'flex', gap: '12px' }}><span style={{ color: '#C5A075', fontWeight: '700', flexShrink: 0, minWidth: '22px' }}>05</span><span>Once satisfied, press <strong>Confirm Design</strong> to finalize your selection.</span></div>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', gap: '12px' }}><span style={{ color: '#C5A075', fontWeight: '700', flexShrink: 0, minWidth: '22px' }}>01</span><span>The leather will display numbered <strong>ZONE</strong> buttons — each marks a premium-quality cut area.</span></div>
                        <div style={{ display: 'flex', gap: '12px' }}><span style={{ color: '#C5A075', fontWeight: '700', flexShrink: 0, minWidth: '22px' }}>02</span><span>Tap a <strong>ZONE N</strong> button to preview exactly where your straps will be cut.</span></div>
                        <div style={{ display: 'flex', gap: '12px' }}><span style={{ color: '#C5A075', fontWeight: '700', flexShrink: 0, minWidth: '22px' }}>03</span><span>Both the short and long straps automatically snap into the correct positions — no dragging needed.</span></div>
                        <div style={{ display: 'flex', gap: '12px' }}><span style={{ color: '#C5A075', fontWeight: '700', flexShrink: 0, minWidth: '22px' }}>04</span><span>Zones that appear <strong>dimmed</strong> have already been reserved and are unavailable.</span></div>
                        <div style={{ display: 'flex', gap: '12px' }}><span style={{ color: '#C5A075', fontWeight: '700', flexShrink: 0, minWidth: '22px' }}>05</span><span>Once you've chosen your zone, press <strong>Confirm Design</strong> to finalize.</span></div>
                    </div>
                )}
            </InfoModal>

            <PickerMinimap
                imageUrl={imageUrl}
                isMinimapFull={isMinimapFull}
                setIsMinimapFull={setIsMinimapFull}
                isMobile={isMobile}
                minimapContainerW={minimapContainerW}
                minimapContainerH={minimapContainerH}
                minimapImageW={minimapImageW}
                minimapImageH={minimapImageH}
                dotShortX={dotShortX}
                dotShortY={dotShortY}
                dotLongX={dotLongX}
                dotLongY={dotLongY}
                boundsShort={boundsShort}
                boundsLong={boundsLong}
                allUsedAreas={allUsedAreas}
                restrictedAreas={restrictedAreas}
                preSelectedAreas={preSelectedAreas}
                cutOption={cutOption}
                preSelectedSize={preSelectedSize}
                hasFreeMovePurchase={hasFreeMovePurchase}
                trueWidth={trueWidth}
                imgDisplayHeight={imgDisplayHeight}
                yScale={yScale}
                vWidth={vWidth}
                vShort={vShort}
                vLong={vLong}
                activePart={activePart}
                setActivePart={setActivePart}
                isDraggingMain={isDraggingMain}
                setIsDraggingMain={setIsDraggingMain}
                isConfirmed={isConfirmed}
                isOrderFinished={isOrderFinished}
                isConfigOpen={isConfigOpen}
                collisionType={collisionType}
                isFreeMoveLocked={isFreeMoveLocked}
                isAllPreselectedUsed={isAllPreselectedUsed}
                borderColor={borderColor}
                handleMinimapDrag={handleMinimapDrag}
                handleMiniScroll={handleMiniScroll}
                minimapShortDotRef={minimapShortDotRef}
                minimapLongDotRef={minimapLongDotRef}
                minimapScrollRef={minimapScrollRef}
                t={t}
            />

            <MobileBottomBar
                cutOption={cutOption}
                setCutOption={setCutOption}
                activePart={activePart}
                setActivePart={setActivePart}
                isConfirmed={isConfirmed}
                hasPreSelectedPurchase={hasPreSelectedPurchase}
                setActiveMobileView={setActiveMobileView}
                setGuideTab={setGuideTab}
                setIsGuideOpen={setIsGuideOpen}
            />

        </div >
    );
};

export default LeatherPicker;